/**
 * FIELD VISIT EXECUTION PAGE
 * 
 * Main container for the 9-step Field Visit system
 * Handles step routing, theme switching, and data management
 * 
 * REBUILT: January 2026 per Document 1 spec
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';

// Components
import StepNavigation from './field-visit/components/StepNavigation';
import Step1ClientIntel from './field-visit/components/Step1ClientIntel';
import Step2ClientDiscovery from './field-visit/components/Step2ClientDiscovery';
import Step3DecisionDrivers from './field-visit/components/Step3DecisionDrivers';
import Step4Measurements from './field-visit/components/Step4Measurements';
import Step5Photos from './field-visit/components/Step5Photos';
import Step6ProductSelection from './field-visit/components/Step6ProductSelection';
import Step7RiskLedger from './field-visit/components/Step7RiskLedger';
import Step8ScopeDescription from './field-visit/components/Step8ScopeDescription';
import Step9ReviewComplete from './field-visit/components/Step9ReviewComplete';

// Hooks and utils
import { useFieldVisit } from './field-visit/hooks/useFieldVisit';
import { getThemeForStep } from './field-visit/constants/themes';
import { DEFAULT_FIELD_VISIT_SETTINGS } from './field-visit/constants/settingsDefaults';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const FieldVisitExecutionPage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();
  
  // Current step
  const [currentStep, setCurrentStep] = useState(1);
  const initialStep = searchParams.get('step');
  
  // Use the field visit hook for all data management
  const {
    fieldVisit,
    context,
    settings,
    loading,
    saving,
    error,
    completedSteps,
    isComplete,
    saveFieldVisit,
    updateStep,
    updateField,
    validateStep,
    completeFieldVisit,
    generateEstimate,
    addArea,
    removeArea,
    updateArea,
    addPhoto,
    removePhoto,
    addRisk,
    removeRisk,
  } = useFieldVisit(appointmentId, sessionToken);

  // Initialize step from URL or field visit data
  useEffect(() => {
    if (initialStep) {
      const step = parseInt(initialStep);
      if (step >= 1 && step <= 9) {
        setCurrentStep(step);
      }
    } else if (fieldVisit?.current_step) {
      setCurrentStep(fieldVisit.current_step);
    }
  }, [initialStep, fieldVisit?.current_step]);

  // Get current theme
  const theme = getThemeForStep(currentStep);
  const isSpecOps = theme.name === 'spec-ops';

  // Navigation handlers
  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    // Validate current step before proceeding
    const errors = validateStep(currentStep);
    if (errors.length > 0 && !isComplete) {
      toast.error(`Please complete required fields: ${errors.join(', ')}`);
      return;
    }
    
    if (currentStep < 9) {
      setCurrentStep(currentStep + 1);
      // Auto-save when moving to next step
      saveFieldVisit().catch(() => {});
    }
  };

  const handleSave = async () => {
    try {
      await saveFieldVisit();
      toast.success('Progress saved');
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const handleComplete = async () => {
    try {
      await completeFieldVisit();
      toast.success('Field visit completed!');
    } catch (err) {
      toast.error('Failed to complete field visit');
    }
  };

  const handleGenerateEstimate = async () => {
    try {
      const result = await generateEstimate();
      toast.success('Estimate generated!');
      // Navigate to estimate
      if (result?.estimate_id) {
        navigate(`/estimating?estimate=${result.estimate_id}`);
      }
    } catch (err) {
      toast.error('Failed to generate estimate');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading field visit...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !fieldVisit) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={handleBack}
            className="px-4 py-2 bg-slate-700 text-slate-200 hover:bg-slate-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render the current step
  const renderStep = () => {
    const commonProps = {
      onSave: handleSave,
      onPrev: handlePrev,
      onNext: handleNext,
      saving,
      isComplete,
    };

    switch (currentStep) {
      case 1:
        return (
          <Step1ClientIntel
            {...commonProps}
            data={fieldVisit?.client_intel || {}}
            context={context}
            onUpdate={updateStep}
            validationErrors={isComplete ? [] : validateStep(1)}
          />
        );
      
      case 2:
        return (
          <Step2ClientDiscovery
            {...commonProps}
            data={fieldVisit?.client_discovery || {}}
            onUpdate={updateStep}
            validationErrors={isComplete ? [] : validateStep(2)}
          />
        );
      
      case 3:
        return (
          <Step3DecisionDrivers
            {...commonProps}
            data={fieldVisit?.decision_drivers || {}}
            onUpdate={updateStep}
            validationErrors={isComplete ? [] : validateStep(3)}
          />
        );
      
      case 4:
        return (
          <Step4Measurements
            {...commonProps}
            data={fieldVisit?.measurements || {}}
            onUpdate={updateStep}
            onAddArea={addArea}
            onRemoveArea={removeArea}
            onUpdateArea={updateArea}
            fieldVisitId={fieldVisit?.field_visit_id}
            validationErrors={isComplete ? [] : validateStep(4)}
          />
        );
      
      case 5:
        return (
          <Step5Photos
            {...commonProps}
            data={fieldVisit?.photos || {}}
            areas={fieldVisit?.measurements?.areas || []}
            measurementData={fieldVisit?.measurements || {}}
            onAddPhoto={addPhoto}
            onRemovePhoto={removePhoto}
            validationErrors={isComplete ? [] : validateStep(5)}
          />
        );
      
      case 6:
        return (
          <Step6ProductSelection
            {...commonProps}
            data={fieldVisit?.product_selections || {}}
            measurementData={fieldVisit?.measurements || {}}
            settings={settings || DEFAULT_FIELD_VISIT_SETTINGS}
            onUpdate={updateStep}
            validationErrors={isComplete ? [] : validateStep(6)}
          />
        );
      
      case 7:
        return (
          <Step7RiskLedger
            {...commonProps}
            data={fieldVisit?.risks || {}}
            areas={fieldVisit?.measurements?.areas || []}
            onAddRisk={addRisk}
            onRemoveRisk={removeRisk}
            onUpdate={updateStep}
            validationErrors={isComplete ? [] : validateStep(7)}
          />
        );
      
      case 8:
        return (
          <Step8ScopeDescription
            {...commonProps}
            data={fieldVisit?.scope_description || {}}
            areas={fieldVisit?.measurements?.areas || []}
            productSelections={fieldVisit?.product_selections || {}}
            clientDiscovery={fieldVisit?.client_discovery || {}}
            onUpdate={updateStep}
            settings={settings || DEFAULT_FIELD_VISIT_SETTINGS}
            validationErrors={isComplete ? [] : validateStep(8)}
          />
        );
      
      case 9:
        return (
          <Step9ReviewComplete
            {...commonProps}
            fieldVisit={fieldVisit}
            completedSteps={completedSteps}
            settings={settings || DEFAULT_FIELD_VISIT_SETTINGS}
            onUpdate={updateStep}
            onComplete={handleComplete}
            onGenerateEstimate={handleGenerateEstimate}
            goToStep={setCurrentStep}
            validationErrors={isComplete ? [] : validateStep(9)}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${isSpecOps ? 'bg-[#020617]' : 'bg-white'}`}>
      {/* Header */}
      <header className={`
        sticky top-0 z-50 px-4 py-3 border-b
        ${isSpecOps 
          ? 'bg-[#0f172a] border-slate-700' 
          : 'bg-white border-slate-200 shadow-sm'
        }
      `}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleBack}
              className={`
                p-2 rounded transition-colors
                ${isSpecOps 
                  ? 'hover:bg-slate-800 text-slate-400' 
                  : 'hover:bg-slate-100 text-slate-600'
                }
              `}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div>
              <h1 className={`
                text-lg font-bold tracking-tight
                ${isSpecOps 
                  ? 'text-emerald-400 font-["Rajdhani"] uppercase tracking-wider' 
                  : 'text-slate-800'
                }
              `}>
                {isSpecOps ? '⚡ FIELD VISIT' : 'Field Visit'}
              </h1>
              {context?.appointment && (
                <p className={`text-sm ${isSpecOps ? 'text-slate-500' : 'text-slate-500'}`}>
                  {context.appointment.lead_contact_name || 'Client'} • {context.appointment.location_address || 'Address'}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isComplete && (
              <span className={`
                px-3 py-1 text-xs font-medium rounded-full
                ${isSpecOps 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' 
                  : 'bg-emerald-100 text-emerald-700'
                }
              `}>
                COMPLETE
              </span>
            )}
            {fieldVisit?.estimate_id && (
              <span className={`
                px-3 py-1 text-xs font-medium rounded-full
                ${isSpecOps 
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40' 
                  : 'bg-purple-100 text-purple-700'
                }
              `}>
                ESTIMATE GENERATED
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Step Navigation */}
      <StepNavigation
        currentStep={currentStep}
        setStep={setCurrentStep}
        completedSteps={completedSteps}
        isLocked={isComplete}
        theme={theme.name}
      />

      {/* Main Content */}
      <main className="pb-8">
        {renderStep()}
      </main>
    </div>
  );
};

export default FieldVisitExecutionPage;
